import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Avatar,
  TextField,
  CircularProgress,
  Select,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch } from 'react-redux';
import { getAssetPrice, postBulkOrder, getRebalance } from '../../Redux/Actions/transactionService';
import ConfirmationModal from './ConfirmationModal';

function getInitials(name) {
  if (!name) return '';
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function groupOrdersByStrategy(orders) {
  const map = new Map();
  orders.forEach(order => {
    const strategyId = order.strategy?.id;
    if (!strategyId) return;
    if (!map.has(strategyId)) {
      map.set(strategyId, {
        strategy: order.strategy,
        orders: [],
        investors: new Map(),
      });
    }
    map.get(strategyId).orders.push(order);
    // Add investor (by user id or email for uniqueness)
    const user = order.userRole?.user;
    if (user) {
      const key = user.id || user.email;
      if (!map.get(strategyId).investors.has(key)) {
        map.get(strategyId).investors.set(key, user);
      }
    }
  });
  return Array.from(map.values());
}

function BatchOrderDetailsModal({ open, handleClose, orders, showBanner }) {
  const dispatch = useDispatch();
  const [orderTypes, setOrderTypes] = useState({}); // {strategyId: 'MKT'|'LMT'}
  const [expandedStrategyId, setExpandedStrategyId] = useState(null);
  const [assetPrices, setAssetPrices] = useState({}); // {symbol: price}
  const [limitPrices, setLimitPrices] = useState({}); // {strategyId: {symbol: price}}
  const [loading, setLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [finalOrders, setFinalOrders] = useState([]);
  const entityId = localStorage.getItem('entId');
  const userInfoString = localStorage.getItem('userInfo');
  const userInfo = userInfoString && userInfoString !== "undefined" ? JSON.parse(userInfoString) : null;
  const role = userInfo?.portal || 'MANAGER';

  // Group orders by strategy
  const strategies = useMemo(() => groupOrdersByStrategy(orders), [orders]);

  // Fetch asset prices for all unique assets in all strategies
  useEffect(() => {
    if (!orders.length) return;
    const assetsMap = new Map();
    const custodianAssetMap = {};
    orders.forEach(order => {
      const custodian = order.strategy?.custodian || 'COINBASE';
      order.strategy?.breakups?.forEach(breakup => {
        const asset = breakup.asset;
        if (!assetsMap.has(asset.symbol)) {
          assetsMap.set(asset.symbol, asset);
        }
        if (!custodianAssetMap[custodian]) {
          custodianAssetMap[custodian] = [];
        }
        if (!custodianAssetMap[custodian].includes(asset.symbol)) {
          custodianAssetMap[custodian].push(asset.symbol);
        }
      });
    });
    const payload = { map: custodianAssetMap };
    setLoading(true);
    dispatch(getAssetPrice(payload)).then(response => {
      setLoading(false);
      if (response.payload && response.payload.statusCode === 201) {
        // Merge price data from all custodians
        const allPrices = {};
        Object.keys(response.payload?.data || {}).forEach(custodian => {
          Object.assign(allPrices, response.payload.data[custodian]);
        });
        setAssetPrices(allPrices);
      }
    });
  }, [orders, dispatch]);

  // Handle order type change
  const handleOrderTypeChange = (strategyId, value) => {
    setOrderTypes(prev => ({ ...prev, [strategyId]: value }));
    // Reset limit prices for this strategy if not LMT
    if (value !== 'LMT') {
      setLimitPrices(prev => {
        const newObj = { ...prev };
        delete newObj[strategyId];
        return newObj;
      });
      // Collapse accordion if it was open for this strategy
      setExpandedStrategyId(prev => (prev === strategyId ? null : prev));
    } else {
      // Open accordion for this strategy
      setExpandedStrategyId(strategyId);
    }
  };

  // Handle limit price input change
  const handleLimitPriceChange = (strategyId, symbol, value) => {
    setLimitPrices(prev => ({
      ...prev,
      [strategyId]: {
        ...prev[strategyId],
        [symbol]: value
      }
    }));
  };

  // Handle expand/collapse
  const handleExpandClick = (strategyId) => {
    setExpandedStrategyId(prev => (prev === strategyId ? null : strategyId));
  };

  // Handle submit order
  const handleSubmit = async () => {
    // Validation: all strategies must have order type selected
    for (const { strategy } of strategies) {
      const strategyId = strategy.id;
      const type = orderTypes[strategyId];
      if (!type) {
        showBanner('Please select order type for all strategies.', 'error');
        return;
      }
      if (type === 'LMT') {
        const breakups = strategy.breakups || [];
        for (const breakup of breakups) {
          const price = limitPrices[strategyId]?.[breakup.asset.symbol];
          if (!price || isNaN(Number(price))) {
            showBanner('Please enter limit price for all assets in LMT order.', 'error');
            return;
          }
        }
      }
    }
    setLoading(true);
    // Build payload according to the required format
    const items = strategies.map(({ strategy, orders }) => {
      const strategyId = strategy.id;
      const type = orderTypes[strategyId] || 'MKT';
      const orderData = {
        orderIds: orders.map(order => order.id), // All order IDs for this strategy
        type,
        orderItems: [],
      };
      if (type === 'LMT') {
        // For LMT, add orderItems for each asset with user input price
        orderData.orderItems = (strategy.breakups || []).map(breakup => ({
          symbol: breakup.asset.symbol,
          price: parseFloat(limitPrices[strategyId]?.[breakup.asset.symbol]),
          isWashTrade: false
        }));
      }
      return orderData;
    });
    const payload = {
      items,
      isPreview: true
    };

    try {
      const response = await dispatch(postBulkOrder(payload));
      setLoading(false);
      if (response.payload && response.payload.statusCode === 200) {
        handleClose();
        setFinalOrders(response.payload?.data);
        setConfirmationOpen(true);
      } else {
        showBanner('Failed to process orders', 'error');
     }
    } catch (error) {
      setLoading(false);
      showBanner('Error occurred while processing orders', 'error');
    }
  };

  // Confirmation modal logic
  const handleConfirm = async (payload) => {
    setLoading(true);
    const finalPayload = { ...payload, isPreview: false };
    try {
        const response = await dispatch(postBulkOrder(finalPayload));
      setLoading(false);
      if (response.payload && response.payload.statusCode === 200) {
        const body = {
          type: 'MANAGER_DISCRETION',
          status: 'PENDING',
        };
        dispatch(getRebalance(body));
        showBanner('Orders placed successfully!', 'success');
      } else {
        showBanner('Failed to place orders.', 'error');
      }
    } catch (error) {
      setLoading(false);
      showBanner(`Error occurred: ${error.message}`, 'error');
    }
    setConfirmationOpen(false);
    handleClose();
  };

  // Compute if submit should be disabled
  const isSubmitDisabled = useMemo(() => {
    for (const { strategy } of strategies) {
      const strategyId = strategy.id;
      const type = orderTypes[strategyId];
      if (!type) return true;
      if (type === 'LMT') {
        const breakups = strategy.breakups || [];
        for (const breakup of breakups) {
          const price = limitPrices[strategyId]?.[breakup.asset.symbol];
          if (!price || isNaN(Number(price))) return true;
        }
      }
    }
    return false;
  }, [strategies, orderTypes, limitPrices]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        sx={{ '& .MuiDialog-paper': { width: '800px', maxWidth: '1700px', borderRadius: 2, p: 0 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 24, pb: 0, pt: 3, pl: 4 }}>
          Order Details
        </DialogTitle>
        <DialogContent sx={{ p: 4, pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {strategies.map(({ strategy, investors }) => {
              const strategyName = strategy?.name || 'Strategy Name';
              const breakups = strategy?.breakups || [];
              const strategyId = strategy?.id;
              const orderType = orderTypes[strategyId] || 'MKT';
              return (
                <Box key={strategyId}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: '1px solid #E0E0E0',
                      borderRadius: 2,
                      bgcolor: '#FAFAFB',
                      px: 3,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      minHeight: 64,
                      boxShadow: 'none',
                      cursor: 'pointer',
                      mb: 2,
                    }}
                    onClick={() => handleExpandClick(strategyId)}
                  >
                    <Typography sx={{ fontWeight: 500, fontSize: 16, flex: 1 }}>
                      {strategyName}
                    </Typography>
                    <FormControl sx={{ minWidth: 220, mr: 2 }} size="small" onClick={e => e.stopPropagation()}>
                      <InputLabel>Choose Order Type</InputLabel>
                      <Select
                        value={orderTypes[strategyId] || ''}
                        label="Choose Order Type"
                        onChange={(e) => handleOrderTypeChange(strategyId, e.target.value)}
                      >
                        <MenuItem value="MKT">Market Order</MenuItem>
                        <MenuItem value="LMT">Limit Order</MenuItem>
                        {/* <MenuItem value="TWAP">TWAP Order</MenuItem> */}
                      </Select>
                    </FormControl>
                    <Box sx={{ flex: '0 0 auto', ml: 'auto', display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={e => { e.stopPropagation(); handleExpandClick(strategyId); }}>
                        {expandedStrategyId === strategyId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Paper>
                  <Collapse in={expandedStrategyId === strategyId} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        border: '1px solid #E0E0E0',
                        borderTop: 'none',
                        borderRadius: '0 0 16px 16px',
                        p: 3,
                        px: 4,
                        display: 'flex',
                        gap: 6,
                        bgcolor: '#FFF',
                        mb: 2
                      }}
                    >
                      {/* Asset Table */}
                      <Box sx={{ flex: 1, minWidth: 400 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>Asset</Typography>
                        <Table size="small" sx={{ background: 'transparent' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Asset</TableCell>
                              <TableCell>Market Price</TableCell>
                              {orderType === 'LMT' && <TableCell>Limit Price</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {breakups.map((breakup, idx) => (
                              <TableRow key={breakup.asset?.symbol || idx}>
                                <TableCell>{breakup.asset?.symbol}</TableCell>
                                <TableCell sx={{ color: '#0066FF', fontWeight: 500 }}>
                                  {assetPrices[breakup.asset?.symbol]
                                    ? `$${Number(assetPrices[breakup.asset?.symbol]).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : '$400,000.00'}
                                </TableCell>
                                {orderType === 'LMT' && (
                                  <TableCell>
                                    <TextField
                                      required
                                      size="small"
                                      type="number"
                                      value={limitPrices[strategyId]?.[breakup.asset.symbol] || ''}
                                      onChange={e => handleLimitPriceChange(strategyId, breakup.asset.symbol, e.target.value)}
                                      inputProps={{ min: 0, step: 'any' }}
                                      placeholder="Enter limit price"
                                    />
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                      {/* Investor List */}
                      <Box sx={{ flex: 1, minWidth: 400 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>Investor List</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {investors.size > 0 ? (
                            Array.from(investors.values()).map((user) => (
                              <Box key={user.id || user.email} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, fontSize: 16 }}>
                                  {getInitials(user.name || user.email)}
                                </Avatar>
                                <Typography>{user.name || user.email}</Typography>
                              </Box>
                            ))
                          ) : (
                            <Typography color="text.secondary">No investor found</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3, pt: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{ minWidth: 140, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, py: 1, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{ minWidth: 180, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, py: 1, px: 3, ml: 2 }}
            disabled={loading || isSubmitDisabled}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Order'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        open={confirmationOpen}
        handleClose={() => setConfirmationOpen(false)}
        handleConfirm={handleConfirm}
        orders={finalOrders}
        userInfo={orders}
        setFinalOrders={setFinalOrders}
      />
      {/* Loader for postOrder API */}
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
}

export default BatchOrderDetailsModal;